/** @format */
import { makeStyles, TableBody, TableCell, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import CustomSmallTextButton from '../../components/Buttons/CustomSmallTextButton';
import EditButton from '../../components/Buttons/EditButton';
import PaperedTable from '../../components/Table/PaperedTable';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { configDefault } from '../../core/config';
import { useDispatch, useSelector } from 'react-redux';
import { store } from '../../core/store';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import I18n from '../../components/Translate/I18n';
import { prefixesConstants, prefixesReducers, prefixesReducersType } from '../../modules/prefixes/reducers';
import { history } from '../../core/history';
import { fractionsReducers } from '../../modules/fractions/reducers';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import Pager from '../../components/Adescom/Pager';
import PrefixInterface from '../../interfaces/billing/PrefixInterface';
import CancelButton from '../../components/Buttons/CancelButton';
import { pricelistSchemaElementPrefixConstants } from '../../modules/pricelists/reducers';
import Typography from '@material-ui/core/Typography';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import AlertConfirm from '../../components/Info/AlertConfirm';
import DeleteButton from '../../components/Buttons/DeleteButton';
import { FilterReducerFieldName } from '../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { PrefixToCsv } from './Import/PrefixUploadReportToCsv';

store.injectReducer('prefixesReducers', prefixesReducers);
store.injectReducer('fractionsReducers', fractionsReducers);

interface PrefixListPropsInterface {
    classes: any;
    renderOutsideButtons?: (el: PrefixInterface) => void;
    hideInsideButtons?: boolean;
}

const PrefixList = (props: PrefixListPropsInterface): React.ReactElement => {
    const classes = props.classes;
    const dispatch = useDispatch();

    const PREFIX_URI = '/prefixes';

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const { collections } = useSelector((state: { prefixesReducers: prefixesReducersType }) => {
        return state.prefixesReducers;
    });

    const [orders, setOrders] = useState<Array<Order>>([]);
    const [filters, setFilters] = useState<Array<Filter>>([]);

    const [selectedPrefixIdToDelete, setSelectedPrefixIdToDelete] = useState<number | null>(null);

    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PREFIX_URI, configDefault.PLATFORM_API_PREFIX)),
            prefixesConstants.LIST,
            [],
            [{ name: 'mainname', value: 'asc' }],
            (data: any) => {},
            {
                propagateDataToCallback: false,
                dispatchAfterCallback: true,
            }
        );
    }, [dispatch]);

    const editPrefix = (param: any): void => history.push(FormGeneralHelper.getRouteUrl(`/prefix-edit/${param.id}`));
    const addPrefix = (): void => history.push(FormGeneralHelper.getRouteUrl('/prefix-add'));
    const importPrefix = (): void => history.push(FormGeneralHelper.getRouteUrl('/prefixes-upload-list'));

    const updateOrderList = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilterList = (filters: Array<Filter>): void => {
        setFilters(filters);
    };

    const clearFilter = (): void => {
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const setDefaultFilter = (callback: any): void => {
        setOrders([]);
        setFilters([]);
        callback();
    };

    const paginationCallbackFunction = (uri?: string | null): void => {
        if (!uri) {
            uri = FormGeneralHelper.getPath(PREFIX_URI, configDefault.PLATFORM_API_PREFIX);
        }

        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            prefixesConstants.LIST,
            filters,
            orders.length > 0 ? orders : [{ name: 'mainname', value: 'asc' }],
            (data: any) => {},
            {
                propagateDataToCallback: false,
                dispatchAfterCallback: true,
            }
        );
    };

    const deletePrefix = (param: { id: number }): void => {
        setSelectedPrefixIdToDelete(param.id);
    };

    const confirmDelete = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        cancelDelete();
        itemLoader.deleteCall(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PREFIX_URI + '/' + selectedPrefixIdToDelete)),
            pricelistSchemaElementPrefixConstants.DELETE,
            {
                onSuccess: (): void => {
                    paginationCallbackFunction();
                    NotificationHelper.notifySuccess('prefixes:notify.delete_prefix_success', dispatch);
                },
                onError: (error: any): void => {
                    console.log(error);
                    if (error && error.message && error.message.message) {
                        NotificationHelper.notifyError(error.message.message, dispatch);
                    } else {
                        NotificationHelper.notifyError('prefixes:notify.error', dispatch);
                    }
                },
            },
            {
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: false,
            }
        );
    };

    const cancelDelete = (): void => {
        setSelectedPrefixIdToDelete(null);
    };

    const exportToCsv = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        const toCsv = new PrefixToCsv(itemLoader);
        toCsv.exportToCsv();
    };

    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <div>
                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PREFIX_POST) && !props.hideInsideButtons && (
                        <>
                            <CustomTextButtom size="lg" color="primary" label={'prefixes:b.add_prefix'} callback={addPrefix} callbackParam={{}} />
                            <CustomTextButtom
                                size="lg"
                                color="primary"
                                label={'prefixes:b.import_prefix'}
                                callback={importPrefix}
                                callbackParam={{}}
                            />
                            <CustomTextButtom
                                size="lg"
                                color="primary"
                                label={'prefixes:b.export_prefix'}
                                callback={exportToCsv}
                                callbackParam={{}}
                            />
                        </>
                    )}

                    <PaperedTable
                        beforeTableContent={
                            <GridContainer>
                                <GridItem xs={12} className={classes.beforeTableContent}>
                                    <CustomTextButtom
                                        callback={paginationCallbackFunction}
                                        callbackParam={null}
                                        size={'lg'}
                                        color="primary"
                                        label="general:common.search"
                                    />
                                </GridItem>
                            </GridContainer>
                        }
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyCommand} padding="none" align="left">
                                    <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                    {(orders.length > 0 || filters.length > 0) && (
                                        <CancelButton
                                            callback={(): void => setDefaultFilter(clearFilter)}
                                            callbackParam={{}}
                                            tooltipTitle="general:common.clear_filter"
                                        />
                                    )}
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="prefixes:h.prefix_name"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="mainname"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="prefixes:h.prefix"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="partialPrefix"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="fractions:f.fraction"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="fractionName"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {collections &&
                                collections['hydra:member'].map((el: PrefixInterface) => {
                                    return (
                                        <TableRow key={`prefix_${el.id}`}>
                                            <TableCell classes={tableCellRoot} align="left">
                                                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PREFIX_PUT) && !props.hideInsideButtons && (
                                                    <EditButton
                                                        tooltipTitle="prefixes:b.edit_prefix"
                                                        callback={editPrefix}
                                                        callbackParam={{ id: el.id }}
                                                    />
                                                )}
                                                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PREFIX_DELETE) && !props.hideInsideButtons && (
                                                    <DeleteButton
                                                        tooltipTitle="prefixes:b.delete_prefix"
                                                        callback={deletePrefix}
                                                        callbackParam={{ id: el.id }}
                                                    />
                                                )}
                                                {props.renderOutsideButtons && props.renderOutsideButtons(el)}
                                                {/* {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PREFIX_DELETE) && (
                                            <DeleteButton
                                                tooltipTitle="prefixes:b.delete_prefix"
                                                callback={deletePrefix}
                                                callbackParam={{ id: el.id }}
                                            />
                                        )} */}
                                            </TableCell>
                                            <TableCell classes={tableCellRoot} align="left">
                                                {el.mainname}
                                            </TableCell>
                                            <TableCell classes={tableCellRoot} align="left">
                                                {el.prefix}
                                            </TableCell>
                                            <TableCell classes={tableCellRoot} align="left">
                                                {el.fractionName}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell classes={tableCellRoot} colSpan={4}>
                                    {collections && collections['hydra:view'] && (
                                        <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </PaperedTable>

                    {selectedPrefixIdToDelete && (
                        <AlertConfirm
                            onConfirm={confirmDelete}
                            onCancel={cancelDelete}
                            confirmBtnText={'general:common.confirm'}
                            cancelBtnText={'general:common.cancel'}
                            content={'prefixes:notify.delete_prefix'}
                        />
                    )}
                </div>
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(PrefixList);
